import { ethers } from 'ethers';
import type { FC, ReactElement } from 'react';
import { useContext, useEffect, useState } from 'react';

import { CoinGeckoContext } from '../coingecko/coingecko.context';
import { MoralisContext } from '../moralis/moralis.context';
import { MarketPlaceContext } from './marketplace.context';

interface IEventIDsToTokens {
  [listingIds: string]: string;
}
interface IListingsToPrice {
  [listingIds: string]: { listingPrice: string; isStarsListing: boolean };
}
interface IAuctionsToPrice {
  [listingIds: string]: { startingPrice: string; isStarsAuction: boolean };
}

interface ILastSoldMap {
  [token: string]: { salePrice: string; isStarsListing: boolean };
}

interface Props {
  children: ReactElement;
}
export const MarketplaceContextProvider: FC<Props> = ({ children }) => {
  const [lastSoldMap, setLastSoldMap] = useState({});
  const [purchasePriceMap, setPurchasePriceMap] = useState({});
  const [notificationEmail, setNotificationEmail] = useState('');
  const { moralis, initialized } = useContext(MoralisContext);
  const { starsUsdRate, ethUsdRate } = useContext(CoinGeckoContext);

  // Build all relavent maps in memory
  useEffect(() => {
    const parseListingCreated = async () => {
      const query = new moralis.Query('MultiListingCreated').limit(1000);
      const results = await query.find();
      // Map listing IDs to tokens
      const listingsToTokens: IEventIDsToTokens = {};
      // Map listing IDs to price
      const listingsToPrice: IListingsToPrice = {};
      for (const i of results) {
        const listingID = i.get('listingId') as string;
        const tokenAddress = i.get('tokenAddress') as string;
        const tokenID = i.get('tokenId') as string;
        const isStarsListing = i.get('isStarsListing') as boolean;
        let listingPrice = '';
        if (isStarsListing) {
          // STARS
          listingPrice = i.get('starsPrice') as string;
        } else {
          // ETH
          listingPrice = i.get('ethPrice') as string;
        }

        listingsToTokens[listingID] = `${tokenAddress}/${tokenID}`;
        listingsToPrice[listingID] = { listingPrice, isStarsListing };
      }
      return { listingsToTokens, listingsToPrice };
    };

    const parseAuctionCreated = async () => {
      const query = new moralis.Query('MultiAuctionCreated').limit(1000);
      const results = await query.find();
      // Map auction IDs to tokens
      const auctionsToTokens: IEventIDsToTokens = {};
      const auctionsToPrice: IAuctionsToPrice = {};
      for (const i of results) {
        const auctionID = i.get('auctionId') as string;
        const tokenAddress = i.get('tokenAddress') as string;
        const tokenID = i.get('tokenId') as string;
        auctionsToTokens[auctionID] = `${tokenAddress}/${tokenID}`;
        const isStarsAuction = i.get('isStarsAuction') as boolean;
        const startingPrice = i.get('startingPrice') as string;
        auctionsToPrice[auctionID] = {
          startingPrice,
          isStarsAuction,
        };
      }
      return { auctionsToTokens, auctionsToPrice };
    };

    const getLastSoldMap = async (
      listingsToTokens: IEventIDsToTokens,
      auctionsToTokens: IEventIDsToTokens,
      listingsToPrice: IListingsToPrice,
      auctionsToPrice: IAuctionsToPrice,
    ) => {
      // We need to find the most recent listingID sale / auctionID claim for each thingy
      const querySaleMade = new moralis.Query('MultiSaleMade')
        .limit(1000)
        .ascending('createdAt');
      const saleMadeEvents = await querySaleMade.find();

      const queryAuctionClaimed = new moralis.Query('MultiAuctionClaimed')
        .limit(1000)
        .ascending('createdAt');
      const auctionClaimedEvents = await queryAuctionClaimed.find();

      // Most recent event could be auction or listing
      const mostRecentEvent: {
        [token: string]: {
          listingId: string | undefined;
          auctionId: string | undefined;
          createdAt: Date;
        };
      } = {};
      for (const saleMadeEvent of saleMadeEvents) {
        const listingId = saleMadeEvent.get('listingId') as string;
        const createdAt = saleMadeEvent.get('createdAt') as Date;
        const token = listingsToTokens[listingId];
        // if empty create
        if (!(token in mostRecentEvent)) {
          mostRecentEvent[token] = {
            listingId,
            auctionId: undefined,
            createdAt,
          };
        }
        // invariant that an entry exists, if createdAt after mostRecentEvent, replace the event

        if (mostRecentEvent[token].createdAt < createdAt) {
          mostRecentEvent[token] = {
            listingId,
            auctionId: undefined,
            createdAt,
          };
        }
      }
      for (const auctionClaimedEvent of auctionClaimedEvents) {
        const auctionId = auctionClaimedEvent.get('auctionId') as string;
        const createdAt = auctionClaimedEvent.get('createdAt') as Date;
        const token = auctionsToTokens[auctionId];
        // if empty create
        if (!(token in mostRecentEvent)) {
          mostRecentEvent[token] = {
            listingId: undefined,
            auctionId,
            createdAt,
          };
        }
        // invariant that an entry exists, if createdAt after mostRecentEvent, replace the event
        if (mostRecentEvent[token].createdAt < createdAt) {
          mostRecentEvent[token] = {
            listingId: undefined,
            auctionId,
            createdAt,
          };
        }
      }

      // we have most recent events now, just need to find their prices haahhahaha
      const queryPriceChanged = new moralis.Query('MultiListingPriceChanged')
        .limit(1000)
        .ascending('createdAt');
      const priceChangedEvents = await queryPriceChanged.find();

      const queryBidPlaced = new moralis.Query('MultiBidPlaced')
        .limit(1000)
        .ascending('createdAt');
      const bidPlacedEvents = await queryBidPlaced.find();

      const newLastSoldMap: ILastSoldMap = {};
      for (const token in mostRecentEvent) {
        const { listingId, auctionId, createdAt } = mostRecentEvent[token];

        if (listingId !== undefined) {
          // Get listing price
          let salePrice = listingsToPrice[listingId].listingPrice;
          // we need to find the price, listingsToPrice is the base price, but it may have been changed
          const priceChangedEventsBeforeSale = priceChangedEvents.filter(
            (i: any) =>
              i.get('listingId') === listingId &&
              i.get('createdAt') < createdAt,
          );
          for (const priceChangeEvent of priceChangedEventsBeforeSale) {
            salePrice = priceChangeEvent.get('newPrice') as string;
          }
          // Add entry
          newLastSoldMap[token] = {
            salePrice,
            isStarsListing: listingsToPrice[listingId].isStarsListing,
          };
        } else if (auctionId !== undefined) {
          // Get auction price
          let floorPrice = auctionsToPrice[auctionId].startingPrice;
          const auctionBids = bidPlacedEvents.filter(
            (i: any) => i.get('auctionId') === auctionId,
          );
          for (const bid of auctionBids) {
            const amount = bid.get('amount') as string;
            if (Number(amount) > Number(floorPrice)) {
              floorPrice = amount;
            }
          }
          newLastSoldMap[token] = {
            salePrice: floorPrice,
            isStarsListing: auctionsToPrice[auctionId].isStarsAuction,
          };
        }
      }

      return newLastSoldMap;
    };

    const getPurchasePriceMap = async (
      listingsToTokens: IEventIDsToTokens,
      listingsToPrice: IListingsToPrice,
    ) => {
      // Need to find all active listings, allListings - canceledlistings - salesClaimed
      const queryListingCreated = new moralis.Query(
        'MultiListingCreated',
      ).limit(1000);
      const listingCreatedEvents = await queryListingCreated.find();

      const querySaleMade = new moralis.Query('MultiSaleMade')
        .limit(1000)
        .ascending('createdAt');
      const saleMadeEvents = await querySaleMade.find();
      const queryListingCancelled = new moralis.Query('MultiListingCancelled')
        .limit(1000)
        .ascending('createdAt');
      const listingCancelledEvents = await queryListingCancelled.find();
      const finishedListingIDs = [];
      for (const i of saleMadeEvents.concat(listingCancelledEvents)) {
        finishedListingIDs.push(i.get('listingId'));
      }

      const activeListingIDs = [];
      for (const i of listingCreatedEvents) {
        const listingID = i.get('listingId') as string;
        if (!finishedListingIDs.includes(listingID)) {
          activeListingIDs.push(listingID);
        }
      }

      // Need to find the most recent price for open listings
      const queryPriceChanged = new moralis.Query('MultiListingPriceChanged')
        .limit(1000)
        .ascending('createdAt');
      const priceChangedEvents = await queryPriceChanged.find();

      const newPurchasePriceMap: IListingsToPrice = {};
      for (const listingID of activeListingIDs) {
        let salePrice = listingsToPrice[listingID].listingPrice;
        const isStarsListing = listingsToPrice[listingID].isStarsListing;

        // we need to find the price, listingsToPrice is the base price, but it may have been changed
        const priceChangedEventsBeforeSale = priceChangedEvents.filter(
          (i: any) => i.get('listingId') === listingID,
        );
        for (const priceChangeEvent of priceChangedEventsBeforeSale) {
          salePrice = priceChangeEvent.get('newPrice') as string;
        }

        const salePriceUSD = isStarsListing
          ? starsUsdRate * Number(ethers.utils.formatEther(salePrice))
          : ethUsdRate * Number(ethers.utils.formatEther(salePrice));

        if (listingsToTokens[listingID] in newPurchasePriceMap) {
          // take min sale price if exists
          const listingIsStars =
            newPurchasePriceMap[listingsToTokens[listingID]].isStarsListing;
          const listingSalePrice =
            newPurchasePriceMap[listingsToTokens[listingID]].listingPrice;
          const listingPriceUSD = listingIsStars
            ? starsUsdRate * Number(ethers.utils.formatEther(listingSalePrice))
            : ethUsdRate * Number(ethers.utils.formatEther(listingSalePrice));

          if (salePriceUSD < listingPriceUSD) {
            newPurchasePriceMap[listingsToTokens[listingID]] = {
              listingPrice: salePrice,
              isStarsListing: listingsToPrice[listingID].isStarsListing,
            };
          }
        } else {
          // else create new one
          newPurchasePriceMap[listingsToTokens[listingID]] = {
            listingPrice: salePrice,
            isStarsListing: listingsToPrice[listingID].isStarsListing,
          };
        }
      }

      return newPurchasePriceMap;
    };

    // setInterval(() => {

    // }, 1000 * 30);
    void (async () => {
      if (initialized) {
        const { listingsToTokens, listingsToPrice } =
          await parseListingCreated();
        const { auctionsToTokens, auctionsToPrice } =
          await parseAuctionCreated();

        const newLastSoldMap = await getLastSoldMap(
          listingsToTokens,
          auctionsToTokens,
          listingsToPrice,
          auctionsToPrice,
        );
        setLastSoldMap(newLastSoldMap);

        // TODO also take auctions int oaccount ?
        const newPurchasePriceMap = await getPurchasePriceMap(
          listingsToTokens,
          listingsToPrice,
        );
        setPurchasePriceMap(newPurchasePriceMap);
      }
    })();
  }, [initialized]);

  return (
    <MarketPlaceContext.Provider
      value={{
        lastSoldMap,
        purchasePriceMap,
        notificationEmail,
        setNotificationEmail,
      }}
    >
      {children}
    </MarketPlaceContext.Provider>
  );
};
